// ============================================================
//  Métricas compartidas por todos los tableros. Fuente única de cada cálculo
//  (ver docs/METRICAS.md). Consume NormalizedTask, nunca BacklogTask crudo.
// ============================================================
import { NormalizedTask } from './normalize';
import { esActivo, esEntregado, EstadoEntrega } from './config';

export interface NameValue {
  name: string;
  value: number;
}

const SIN = '(sin asignar)';

/** Agrupa sumando una métrica numérica; ordena desc. */
export function sumBy(
  arr: NormalizedTask[],
  key: (t: NormalizedTask) => string,
  val: (t: NormalizedTask) => number,
  fallback = SIN
): NameValue[] {
  const m = new Map<string, number>();
  for (const t of arr) {
    const k = key(t) || fallback;
    m.set(k, (m.get(k) || 0) + val(t));
  }
  return Array.from(m, ([name, value]) => ({ name, value: Math.round(value * 10) / 10 })).sort(
    (a, b) => b.value - a.value
  );
}

/** Agrupa contando; ordena desc. */
export function countBy(
  arr: NormalizedTask[],
  key: (t: NormalizedTask) => string,
  fallback = SIN
): NameValue[] {
  return sumBy(arr, key, () => 1, fallback);
}


export function metricValue(t: NormalizedTask, metric: 'tareas' | 'horas'): number {
  return metric === 'horas' ? t.horas_totales : 1;
}

export function metricLabel(metric: 'tareas' | 'horas'): string {
  return metric === 'horas' ? 'Horas' : 'Tareas';
}

export function fmtMetricValue(value: number, metric: 'tareas' | 'horas'): number {
  return metric === 'horas' ? Math.round(value * 10) / 10 : Math.round(value);
}

export function mean(values: number[]): number | null {
  if (!values.length) return null;
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
}

export function median(values: number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  const m = s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
  return Math.round(m * 10) / 10;
}

export function bucketize(values: number[], edges: number[], labels: string[]): NameValue[] {
  const out = labels.map((name) => ({ name, value: 0 }));
  for (const v of values) {
    let i = edges.findIndex((e) => v <= e);
    if (i === -1) i = labels.length - 1;
    out[i].value++;
  }
  return out;
}

/** Excluye canceladas/rechazadas/pend.aprobación (carga real de trabajo). */
export const cargaReal = (tasks: NormalizedTask[]) => tasks.filter((t) => !t.excluido);

// ---- KPIs principales ----
export interface Kpis {
  qtyTareas: number;
  qtyConRetrabajo: number;   // tareas reabiertas al menos una vez (retrabajo interno)
  retrabajosTotales: number; // suma de reaperturas de todas las tareas
  retrabajoProm: number;     // reaperturas promedio por tarea
  pctConRetrabajo: number;   // % de tareas con al menos una reapertura
  qtyAjustes: number;        // tareas que son ajuste pedido por el cliente (campo ajuste)
  pctAjustes: number;        // % de tareas que son ajuste de cliente
  horasTotales: number;
  horasEstimadas: number;
  horasReales: number;
  qtyUrgentes: number;
  pctUrgentes: number;
  qtyExtraordinarias: number;
  pctExtraordinarias: number;
  qtyEntregadas: number;
  pctATiempo: number;
  pctTarde: number;
  demoraProm: number | null;
  demoraMediana: number | null;
  antelacionProm: number | null;
  antelacionMediana: number | null;
  backlog: number;
  vencidas: number;
  proximas: number;
}

export function computeKpis(all: NormalizedTask[]): Kpis {
  const tasks = cargaReal(all);
  const n = tasks.length;
  const conRetrabajo = tasks.filter((t) => t.tiene_retrabajo).length;
  const retrabajosTotales = tasks.reduce((s, t) => s + t.retrabajos_count, 0);
  const ajustesCliente = tasks.filter((t) => t.es_ajuste).length;
  const urgentes = tasks.filter((t) => t.es_urgente).length;
  const extra = tasks.filter((t) => t.es_extraordinaria).length;

  const entregadas = tasks.filter((t) => esEntregado(t.estado));
  const aTiempo = entregadas.filter((t) => t.estado_entrega === 'a_tiempo' || t.estado_entrega === 'adelantada').length;
  const tarde = entregadas.filter((t) => t.estado_entrega === 'tarde').length;

  const demoras = tasks.map((t) => t.dias_demora).filter((d): d is number => d != null && d >= 0);
  const antel = tasks.map((t) => t.dias_antelacion).filter((d): d is number => d != null);

  const pct = (x: number, d: number) => (d ? Math.round((x / d) * 1000) / 10 : 0);

  return {
    qtyTareas: n,
    qtyConRetrabajo: conRetrabajo,
    retrabajosTotales,
    retrabajoProm: n ? Math.round((retrabajosTotales / n) * 10) / 10 : 0,
    pctConRetrabajo: pct(conRetrabajo, n),
    qtyAjustes: ajustesCliente,
    pctAjustes: pct(ajustesCliente, n),
    horasTotales: Math.round(tasks.reduce((s, t) => s + t.horas_totales, 0) * 10) / 10,
    horasEstimadas: Math.round(tasks.reduce((s, t) => s + t.horas_estimadas, 0) * 10) / 10,
    horasReales: Math.round(tasks.reduce((s, t) => s + t.horas_reales, 0) * 10) / 10,
    qtyUrgentes: urgentes,
    pctUrgentes: pct(urgentes, n),
    qtyExtraordinarias: extra,
    pctExtraordinarias: pct(extra, n),
    qtyEntregadas: entregadas.length,
    pctATiempo: pct(aTiempo, entregadas.length),
    pctTarde: pct(tarde, entregadas.length),
    demoraProm: mean(demoras),
    demoraMediana: median(demoras),
    antelacionProm: mean(antel),
    antelacionMediana: median(antel),
    backlog: tasks.filter((t) => esActivo(t.estado)).length,
    vencidas: tasks.filter((t) => t.estado_entrega === 'vencido').length,
    proximas: tasks.filter((t) => t.estado_entrega === 'proximo').length,
  };
}

// ---- Distribución por estado de entrega ----
export function porEstadoEntrega(all: NormalizedTask[]): NameValue[] {
  return countBy(cargaReal(all), (t) => t.estado_entrega);
}

// ---- Retrabajo (reaperturas: contador de veces que la tarea volvió de done a activo) ----
export interface RetrabajoMetrics {
  distribucion: NameValue[]; // Con vs Sin retrabajo
  topTareas: NormalizedTask[];
  porResponsable: NameValue[];
  porProyecto: NameValue[];
  porArea: NameValue[];
}

export function computeRetrabajo(all: NormalizedTask[]): RetrabajoMetrics {
  const tasks = cargaReal(all);
  const conRetrabajo = tasks.filter((t) => t.tiene_retrabajo);
  return {
    distribucion: [
      { name: 'Sin retrabajo', value: tasks.filter((t) => !t.tiene_retrabajo).length },
      { name: 'Con retrabajo', value: conRetrabajo.length },
    ],
    topTareas: [...conRetrabajo].sort((a, b) => b.retrabajos_count - a.retrabajos_count).slice(0, 15),
    porResponsable: sumBy(conRetrabajo, (t) => t.responsable, (t) => t.retrabajos_count),
    porProyecto: sumBy(conRetrabajo, (t) => t.proyecto, (t) => t.retrabajos_count, '(sin proyecto)'),
    porArea: sumBy(conRetrabajo, (t) => t.area_produccion, (t) => t.retrabajos_count, '(sin área)'),
  };
}

// ---- Ajuste de cliente (tareas que son ajuste de una anterior: campo `ajuste`) ----
export interface AjustesClienteMetrics {
  distribucion: NameValue[]; // Original vs Ajuste de otra (conteo de tareas)
  porSolicitante: NameValue[];
  porProyecto: NameValue[];
  topTareas: NormalizedTask[]; // tareas que son ajuste, más recientes primero
}

export function computeAjustesCliente(all: NormalizedTask[]): AjustesClienteMetrics {
  const tasks = cargaReal(all);
  const ajustes = tasks.filter((t) => t.es_ajuste);
  return {
    distribucion: [
      { name: 'Original', value: tasks.length - ajustes.length },
      { name: 'Ajuste de otra', value: ajustes.length },
    ],
    porSolicitante: countBy(ajustes, (t) => t.solicitante),
    porProyecto: countBy(ajustes, (t) => t.proyecto, '(sin proyecto)'),
    topTareas: [...ajustes].sort((a, b) => (b.fecha_solicitud || '').localeCompare(a.fecha_solicitud || '')).slice(0, 15),
  };
}

// ---- Carga por persona (Tablero 6) ----
export interface CargaPersona {
  responsable: string;
  tareas: number;
  enCurso: number;
  vencidas: number;
  proximas: number;
  urgentes: number;
  horas: number;
  pctHoras: number;
}

export function computeCarga(all: NormalizedTask[]): CargaPersona[] {
  const tasks = cargaReal(all);
  const totalHoras = tasks.reduce((s, t) => s + t.horas_totales, 0);
  const m = new Map<string, CargaPersona>();
  for (const t of tasks) {
    const k = t.responsable || SIN;
    const cur =
      m.get(k) ||
      { responsable: k, tareas: 0, enCurso: 0, vencidas: 0, proximas: 0, urgentes: 0, horas: 0, pctHoras: 0 };
    cur.tareas++;
    if (esActivo(t.estado)) cur.enCurso++;
    if (t.estado_entrega === 'vencido') cur.vencidas++;
    if (t.estado_entrega === 'proximo') cur.proximas++;
    if (t.es_urgente) cur.urgentes++;
    cur.horas += t.horas_totales;
    m.set(k, cur);
  }
  return Array.from(m.values())
    .map((c) => ({
      ...c,
      horas: Math.round(c.horas * 10) / 10,
      pctHoras: totalHoras ? Math.round((c.horas / totalHoras) * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.horas - a.horas);
}

// ---- Serie mensual genérica (volumen / horas / urgentes) ----
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function monthKey(iso: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(key: string): string {
  const [y, m] = key.split('-');
  return `${MESES[Number(m) - 1]} ${y.slice(2)}`;
}

export interface SerieMes {
  mes: string;
  tareas: number;
  horas: number;
  urgentes: number;
  extraordinarias: number; // tareas extraordinarias del mes
  retrabajos: number; // reaperturas del mes (retrabajo interno)
  ajustes: number;    // tareas que son ajuste de cliente en el mes
}

export function serieMensual(all: NormalizedTask[]): SerieMes[] {
  const tasks = cargaReal(all);
  const m = new Map<string, SerieMes>();
  for (const t of tasks) {
    const k = monthKey(t.fecha_solicitud);
    if (!k) continue;
    const cur = m.get(k) || { mes: k, tareas: 0, horas: 0, urgentes: 0, extraordinarias: 0, retrabajos: 0, ajustes: 0 };
    cur.tareas++;
    cur.horas += t.horas_totales;
    if (t.es_urgente) cur.urgentes++;
    if (t.es_extraordinaria) cur.extraordinarias++;
    cur.retrabajos += t.retrabajos_count;
    if (t.es_ajuste) cur.ajustes++;
    m.set(k, cur);
  }
  return Array.from(m.keys())
    .sort()
    .map((k) => {
      const v = m.get(k)!;
      return { ...v, mes: monthLabel(k), horas: Math.round(v.horas * 10) / 10 };
    });
}

/** Tabla área × dificultad para barras apiladas (Tablero 1 y 2). */
export function areaPorDificultad(all: NormalizedTask[]): { name: string; baja: number; media: number; alta: number; sin: number }[] {
  const tasks = cargaReal(all);
  const m = new Map<string, { name: string; baja: number; media: number; alta: number; sin: number }>();
  for (const t of tasks) {
    const k = t.area_produccion || '(sin área)';
    const cur = m.get(k) || { name: k, baja: 0, media: 0, alta: 0, sin: 0 };
    if (t.dificultad === 'baja') cur.baja++;
    else if (t.dificultad === 'media') cur.media++;
    else if (t.dificultad === 'alta') cur.alta++;
    else cur.sin++;
    m.set(k, cur);
  }
  return Array.from(m.values()).sort((a, b) => b.baja + b.media + b.alta - (a.baja + a.media + a.alta));
}


/** Tabla área × dificultad para barras apiladas en tareas u horas. */
export function areaPorDificultadMetric(
  all: NormalizedTask[],
  metric: 'tareas' | 'horas'
): { name: string; baja: number; media: number; alta: number; sin: number }[] {
  const tasks = cargaReal(all);
  const m = new Map<string, { name: string; baja: number; media: number; alta: number; sin: number }>();
  for (const t of tasks) {
    const k = t.area_produccion || '(sin área)';
    const cur = m.get(k) || { name: k, baja: 0, media: 0, alta: 0, sin: 0 };
    const v = metricValue(t, metric);
    if (t.dificultad === 'baja') cur.baja += v;
    else if (t.dificultad === 'media') cur.media += v;
    else if (t.dificultad === 'alta') cur.alta += v;
    else cur.sin += v;
    m.set(k, cur);
  }
  return Array.from(m.values())
    .map((r) => ({
      name: r.name,
      baja: fmtMetricValue(r.baja, metric),
      media: fmtMetricValue(r.media, metric),
      alta: fmtMetricValue(r.alta, metric),
      sin: fmtMetricValue(r.sin, metric),
    }))
    .sort((a, b) => b.baja + b.media + b.alta + b.sin - (a.baja + a.media + a.alta + a.sin));
}

export function originalVsAjuste(all: NormalizedTask[], metric: 'tareas' | 'horas' = 'tareas'): NameValue[] {
  const tasks = cargaReal(all);
  const originales = tasks.filter((t) => !t.es_ajuste);
  const ajustes = tasks.filter((t) => t.es_ajuste);
  return [
    { name: 'Original', value: fmtMetricValue(originales.reduce((s, t) => s + metricValue(t, metric), 0), metric) },
    { name: 'Ajuste', value: fmtMetricValue(ajustes.reduce((s, t) => s + metricValue(t, metric), 0), metric) },
  ];
}

// ============================================================
//  Revisión de cliente (aprobado / rechazado / pendiente)
// ============================================================
export interface RevisionKpis {
  revisadas: number;
  aprobadas: number;
  rechazadas: number;
  pendientes: number;
  pctAprobacion: number; // sobre revisadas
}

export function revisionKpis(all: NormalizedTask[]): RevisionKpis {
  let aprobadas = 0, rechazadas = 0, pendientes = 0;
  for (const t of all) {
    if (t.revision_valoracion === 'aprobado') aprobadas++;
    else if (t.revision_valoracion === 'rechazado') rechazadas++;
    else if (t.revision_status === 'pending') pendientes++;
  }
  const revisadas = aprobadas + rechazadas;
  return {
    revisadas, aprobadas, rechazadas, pendientes,
    pctAprobacion: revisadas ? Math.round((aprobadas / revisadas) * 100) : 0,
  };
}

export function aprobadoVsRechazado(all: NormalizedTask[]): NameValue[] {
  const k = revisionKpis(all);
  return [
    { name: 'Aprobado', value: k.aprobadas },
    { name: 'Rechazado', value: k.rechazadas },
  ];
}

export interface RevisionProyectoRow {
  name: string;
  aprobado: number;
  rechazado: number;
  pendiente: number;
}

export function revisionPorProyecto(all: NormalizedTask[]): RevisionProyectoRow[] {
  const m = new Map<string, RevisionProyectoRow>();
  for (const t of all) {
    const rs = t.revision_status;
    if (!rs || rs === 'disabled') continue; // solo revisiones activas/terminales
    const key = t.proyecto || '(sin proyecto)';
    if (!m.has(key)) m.set(key, { name: key, aprobado: 0, rechazado: 0, pendiente: 0 });
    const row = m.get(key)!;
    if (t.revision_valoracion === 'aprobado') row.aprobado++;
    else if (t.revision_valoracion === 'rechazado') row.rechazado++;
    else if (rs === 'pending') row.pendiente++;
  }
  return [...m.values()].sort(
    (a, b) => (b.aprobado + b.rechazado + b.pendiente) - (a.aprobado + a.rechazado + a.pendiente)
  );
}

// ============================================================
//  Progreso de proyectos (Progreso por espacio)
// ============================================================

/** Índice de mes continuo (año*12+mes) desde un ISO; null si inválido. */
export function monthIndex(iso?: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.getFullYear() * 12 + d.getMonth();
}

/** Etiqueta corta ("sep 26") desde un índice de mes continuo. */
export function monthShortLabel(idx: number): string {
  return `${MESES[((idx % 12) + 12) % 12]} ${String(Math.floor(idx / 12)).slice(2)}`;
}

export interface ProyectoProgreso {
  name: string;
  estado: string;        // estado del proyecto (catálogo); '' si no está
  total: number;         // tareas del proyecto en la vista (carga real)
  done: number;          // tareas finalizadas
  abiertas: number;      // tareas activas (pending/in_progress/paused)
  pct: number;           // % concluido = done / total
  porEstado: NameValue[]; // desglose por estado de tarea (claves crudas), desc
  startI: number | null; // primer mes (solicitud/inicio)
  endI: number | null;   // último mes (deadline/entrega/solicitud)
}

/**
 * Progreso por proyecto: qué porcentaje de tareas se concluyó sobre el total
 * del proyecto, con el desglose por estado de tarea y el rango temporal para
 * el Gantt. Excluye canceladas/rechazadas/pend. aprobación (carga real).
 */
export function progresoProyectos(
  all: NormalizedTask[],
  proyectoStatus?: Map<string, string>
): ProyectoProgreso[] {
  const tasks = cargaReal(all).filter((t) => t.proyecto);
  const m = new Map<string, {
    total: number; done: number; abiertas: number;
    porEstado: Map<string, number>; startI: number | null; endI: number | null;
  }>();
  for (const t of tasks) {
    const cur = m.get(t.proyecto) ||
      { total: 0, done: 0, abiertas: 0, porEstado: new Map<string, number>(), startI: null, endI: null };
    cur.total += 1;
    if (t.estado === 'done') cur.done += 1;
    if (t.estado === 'pending' || t.estado === 'in_progress' || t.estado === 'paused') cur.abiertas += 1;
    cur.porEstado.set(t.estado, (cur.porEstado.get(t.estado) || 0) + 1);
    const s = monthIndex(t.fecha_solicitud || t.fecha_inicio);
    const e = monthIndex(t.deadline || t.fecha_entrega || t.fecha_solicitud);
    if (s != null && (cur.startI == null || s < cur.startI)) cur.startI = s;
    if (e != null && (cur.endI == null || e > cur.endI)) cur.endI = e;
    m.set(t.proyecto, cur);
  }
  return [...m.entries()]
    .map(([name, v]) => ({
      name,
      estado: proyectoStatus?.get(name) || '',
      total: v.total,
      done: v.done,
      abiertas: v.abiertas,
      pct: v.total ? Math.round((v.done / v.total) * 100) : 0,
      porEstado: Array.from(v.porEstado, ([n, value]) => ({ name: n, value })).sort((a, b) => b.value - a.value),
      startI: v.startI,
      endI: v.endI,
    }))
    .sort((a, b) => b.total - a.total);
}

/** Cuenta cuántos proyectos hay por estado, a partir del progreso ya calculado. */
export function proyectosPorEstado(progreso: ProyectoProgreso[]): NameValue[] {
  const m = new Map<string, number>();
  for (const p of progreso) {
    const k = p.estado || 'sin estado';
    m.set(k, (m.get(k) || 0) + 1);
  }
  return Array.from(m, ([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

// ---- Evolutivo mensual de tareas por proyecto (top-N + "Otros") ----
export interface SerieProyectoMes {
  mes: string;
  [proyecto: string]: number | string;
}

/**
 * Serie mensual (por fecha de solicitud) con una columna por proyecto. Los
 * proyectos fuera del top-N (por volumen) se agrupan en "Otros" para no pasar
 * la paleta categórica. Devuelve las filas y el orden fijo de claves a pintar.
 */
export function serieMensualPorProyecto(
  all: NormalizedTask[],
  topN = 8,
  metric: 'tareas' | 'horas' = 'tareas'
): { data: SerieProyectoMes[]; proyectos: string[] } {
  const OTROS = 'Otros';
  const tasks = cargaReal(all);

  // Ranking de proyectos por volumen para elegir el top-N.
  const totales = new Map<string, number>();
  for (const t of tasks) {
    const k = t.proyecto || '(sin proyecto)';
    totales.set(k, (totales.get(k) || 0) + metricValue(t, metric));
  }
  const ranked = [...totales.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  const top = new Set(ranked.slice(0, topN));
  const hayOtros = ranked.length > topN;
  const keys = [...ranked.slice(0, topN), ...(hayOtros ? [OTROS] : [])];

  const byMonth = new Map<string, SerieProyectoMes>();
  for (const t of tasks) {
    const mk = monthKey(t.fecha_solicitud);
    if (!mk) continue;
    const proj = t.proyecto || '(sin proyecto)';
    const key = top.has(proj) ? proj : OTROS;
    const row = byMonth.get(mk) || ({ mes: mk } as SerieProyectoMes);
    row[key] = (Number(row[key]) || 0) + metricValue(t, metric);
    byMonth.set(mk, row);
  }
  const data = [...byMonth.keys()].sort().map((mk) => {
    const row = byMonth.get(mk)!;
    const out: SerieProyectoMes = { mes: monthLabel(mk) };
    for (const k of keys) out[k] = fmtMetricValue(Number(row[k]) || 0, metric);
    return out;
  });
  return { data, proyectos: keys };
}

// ---- Evolutivo mensual de tareas ingresadas por tipo ----
export interface SerieTipoMes {
  mes: string;
  ordinaria: number;
  extraordinaria: number;
}

/**
 * Tareas ingresadas por mes (fecha de solicitud), separadas por tipo. Mide el
 * flujo de entrada de trabajo: ordinaria (planificada) vs extraordinaria.
 */
export function serieMensualPorTipo(all: NormalizedTask[]): SerieTipoMes[] {
  const tasks = cargaReal(all);
  const m = new Map<string, SerieTipoMes>();
  for (const t of tasks) {
    const mk = monthKey(t.fecha_solicitud);
    if (!mk) continue;
    const cur = m.get(mk) || { mes: mk, ordinaria: 0, extraordinaria: 0 };
    if (t.es_extraordinaria) cur.extraordinaria += 1;
    else cur.ordinaria += 1;
    m.set(mk, cur);
  }
  return [...m.keys()].sort().map((mk) => {
    const v = m.get(mk)!;
    return { ...v, mes: monthLabel(mk) };
  });
}

/** Top-N de un grupo filtrando un predicado (urgentes, extraordinarias). */
export function topN(
  all: NormalizedTask[],
  pred: (t: NormalizedTask) => boolean,
  key: (t: NormalizedTask) => string,
  n = 5
): NameValue[] {
  return countBy(cargaReal(all).filter(pred), key).slice(0, n);
}

export const ESTADO_ENTREGA_ORDER: EstadoEntrega[] = [
  'adelantada', 'a_tiempo', 'tarde', 'vencido', 'proximo', 'en_curso', 'sin_deadline', 'no_aplica',
];
